import { observer } from "mobx-react";
import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import Gravatar from "react-gravatar";
import { useTranslation } from "react-i18next";
import { useAppContext } from "@state/hooks/shared";
import classNames from "classnames";

import { THEME_SETTINGS } from "@theme-settings";
import { useRootAppStore } from "@hooks";
import OutsideClickDetector from "@components-shared/OutsideClickDetector";
import { WrappedLink } from "@components/navigation";
import {
	HeaderLockButton,
	HeaderUserMenu,
	HeaderFailedLoginsButton,
	HeaderUserBalanceDisplay,
} from ".";
import { getLoginReturnUrl } from "@utils/auth";

export default observer(function HeaderUserDetails() {
	const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

	const { t } = useTranslation();
	const { culture } = useAppContext();
	const { pathname } = useLocation();
	const rootStore = useRootAppStore();

	const baseAvatarClasses = classNames("header__avatar", {
		"is-active": isUserMenuOpen,
	});

	if (rootStore.userAuthStore?.user) {
		return (
			<>
				{(THEME_SETTINGS.isOasisEnabled ||
					THEME_SETTINGS.is24hLockEnabled) && <HeaderLockButton />}

				{THEME_SETTINGS.failedLoginReport.isEnabled && (
					<HeaderFailedLoginsButton />
				)}

				<OutsideClickDetector
					onOutsideClick={() => setIsUserMenuOpen(false)}
					wrapperClasses="header__user"
				>
					<>
						<div className="usermenu">
							<div className="usermenu__title">
								{rootStore.userAuthStore.user.username}
							</div>
							<ul className="usermenu__wallets usermenu__wallets--sml">
								<HeaderUserBalanceDisplay isInMenu={false} />
							</ul>
						</div>

						{rootStore.userAuthStore.isUserCritical ? (
							<div
								className={classNames(
									baseAvatarClasses,
									"header__avatar--critical"
								)}
								onClick={() =>
									setIsUserMenuOpen(!isUserMenuOpen)
								}
							>
								<div className="header__tag header__tag--critical" />

								<Gravatar
									email={rootStore.userAuthStore.user.email}
									default="mp"
									className="header__avatar__thumb"
								/>
							</div>
						) : rootStore.userAuthStore.isUserWarning ? (
							<div
								className={classNames(
									baseAvatarClasses,
									"header__avatar--warning"
								)}
								onClick={() =>
									setIsUserMenuOpen(!isUserMenuOpen)
								}
							>
								<div className="header__tag header__tag--warning" />

								<Gravatar
									email={rootStore.userAuthStore.user.email}
									default="mp"
									className="header__avatar__thumb"
								/>
							</div>
						) : (
							<div
								className={baseAvatarClasses}
								onClick={() =>
									setIsUserMenuOpen(!isUserMenuOpen)
								}
							>
								<Gravatar
									email={rootStore.userAuthStore.user.email}
									default="mp"
									className="header__avatar__thumb"
								/>
							</div>
						)}

						<HeaderUserMenu
							isUserMenuOpen={isUserMenuOpen}
							setIsUserMenuOpen={setIsUserMenuOpen}
						/>
					</>
				</OutsideClickDetector>
			</>
		);
	}
	return (
		<div className="header__user">
			<WrappedLink
				to={getLoginReturnUrl()}
				className={
					"header__user__cta header__user__login btn btn--sml" +
					(!WEBPACK_IS_REGISTRATION_ENABLED ||
					pathname === `/${culture}/auth/register`
						? " btn--accent"
						: " btn--primary--inverted") +
					(THEME_SETTINGS.licensingIcons.includes("ggl")
						? " btn--icon"
						: "")
				}
			>
				<>
					<span>{t("HEADER.LOGIN")}</span>

					{THEME_SETTINGS.licensingIcons.includes("ggl") && (
						<i className="u-icon u-icon--xmed u-icon--age-restriction" />
					)}
				</>
			</WrappedLink>

			<RegistrationButton />
		</div>
	);
});

function RegistrationButton() {
	const { culture } = useAppContext();
	const { t } = useTranslation();
	const { pathname } = useLocation();

	if (
		!WEBPACK_IS_REGISTRATION_ENABLED ||
		pathname === `/${culture}/auth/register`
	) {
		return null;
	}

	return (
		<WrappedLink
			to={`/${culture}/auth/register`}
			className="header__user__cta btn btn--sml btn--accent"
		>
			{t("COMMON.OPEN_ACCOUNT_LINK")}
		</WrappedLink>
	);
}
